import React, { forwardRef, useState, useEffect, useContext } from 'react'
import {
  Dialog,
  DialogContent,
  IconButton,
  Grow,
  Box,
  Typography,
  Button,
  Grid,
  Snackbar,
} from '@mui/material'
import { motion } from 'framer-motion'
import { Close, ContentCopy } from '@mui/icons-material'

import { AnimatePresence } from 'framer-motion'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { selectData } from 'layouts/onboarding'
import giftcard from 'assets/images/illustrations/giftcard.svg'
import { callZapier } from 'utils/zapier/callZapier'
import { IntakeContext } from 'context/IntakeContext'
import { useFormDebounce } from 'hooks/useFormDebounce'
import email from 'assets/images/email.png'
import ScrollingLogos from 'components/ScrollingLogos'

const Transition = forwardRef(function Transition(props, ref) {
  return <Grow timeout={300} ref={ref} {...props} />
})

export default function Referral({ onClose }) {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const { updateIntakeStateAndSubmit } = useFormDebounce()

  const [direction, setDirection] = useState(1)
  const variants = {
    enter: (dir) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
    }),
  }

  const [name, setName] = useState('')
  const [referralEmail, setReferralEmail] = useState('')
  const [referralPhone, setReferralPhone] = useState('')
  const [giftCard, setGiftCard] = useState('')

  const [isDisabled, setIsDisabled] = useState(true)

  const [viewEmail, setViewEmail] = useState(false)
  const [sent, setSent] = useState(false)

  useEffect(() => {
    if (
      name != '' &&
      giftCard != '' &&
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(referralEmail)
    ) {
      setIsDisabled(false)
    } else {
      setIsDisabled(true)
    }
  }, [name, referralEmail, referralPhone, giftCard])

  const handleSubmit = () => {
    const referral = {
      name: name || 'No Name',
      email: referralEmail || 'No Email',
      phone: referralPhone || 'No Phone',
    }

    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/2nntpi3/',
      payload: {
        referral: {
          referree: referral,
          referrer: {
            name: intakeState?.firstName + ' ' + intakeState?.lastName,
            email: intakeState?.email,
            phone: intakeState?.phone,
            giftCard: giftCard || intakeState?.giftCard,
          },
        },
      },
      notificationMessage: `We have sent the referral email to ${name}`,
      sendNotification: false,
    })

    setSent(true)
  }

  const slugify = (str) =>
    str
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumerics with -
      .replace(/^-+|-+$/g, '') // Remove leading/trailing dashes

  useEffect(() => {
    if (
      !intakeState?.referralCode &&
      intakeState?.firstName &&
      intakeState?.lastName
    ) {
      const slugFirst = slugify(intakeState.firstName)
      const slugLast = slugify(intakeState.lastName)
      const randomPart = Math.random().toString(36).slice(-6)

      const generatedCode = `${slugFirst}-${slugLast}-${randomPart}`

      updateIntakeStateAndSubmit(() =>
        updateIntakeStateAtKey({ referralCode: generatedCode })
      )()
    }
  }, [intakeState?.referralCode, intakeState?.firstName, intakeState?.lastName])

  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const referralUrl = `https://app.goparco.com?ref=${
    intakeState?.referralCode || ''
  }`

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        TransitionComponent={Transition}
        fullWidth
        maxWidth={'md'}
        PaperProps={{
          sx: {
            overflow: { xs: 'auto', sm: 'visible' },
            margin: '16px',
            width: 'calc(100% - 32px)',
            '@media (max-height:960px)': {
              overflow: 'auto',
            },
          },
        }}
      >
        <Box
          sx={{
            justifyContent: 'space-between',
            position: 'relative',
            display: 'flex',
            top: { xs: 8, sm: -48 },
            '@media (max-height:960px)': {
              top: 8,
            },
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 0,
              right: { xs: 8, sm: 0 },
              color: '#fff',
              zIndex: 1,
              backgroundColor: '#000',
              '&:hover': {
                backgroundColor: '#000',
              },
              '@media (max-height:960px)': {
                right: 8,
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
        <DialogContent sx={{ overflow: 'visible' }}>
          <motion.div
            key="accounts"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
                style={{
                  position: 'relative',
                  width: '100%',
                }}
              >
                <Box
                  px={{ xs: 0, sm: 6 }}
                  pt={{ xs: 4, sm: 6 }}
                  pb={{ xs: 2, sm: 6 }}
                >
                  {!sent && (
                    <>
                      {!viewEmail && (
                        <>
                          <Typography
                            variant="headerTitle"
                            color="#262627"
                            textAlign="center"
                            display="block"
                          >
                            Refer A Friend!
                          </Typography>
                          <Typography
                            variant="body1"
                            mt={1}
                            color="#495057"
                            textAlign="center"
                          >
                            When they sign up with PARCO and meet with a team
                            member, you will both receive a{' '}
                            <strong>$25 Gift Card</strong> to your Favorite
                            Place
                          </Typography>
                          <ScrollingLogos />
                          <Typography
                            variant="body1Medium"
                            mt={2}
                            color={'#495057'}
                            textAlign="left"
                          >
                            Referral Details
                          </Typography>
                          <Grid
                            container
                            mt={0}
                            spacing={3}
                            justifyContent={'center'}
                          >
                            <Grid item xs={6} mb={2}>
                              {renderMuiFormField({
                                inputType: 'text',
                                autoFocus: false,
                                required: true,
                                label: 'Name of Referral',
                                placeholder: 'Fox Mulder',
                                value: name,
                                onChange: setName,
                              })}
                            </Grid>
                            <Grid item xs={6} mb={2}>
                              {renderMuiFormField({
                                autoFocus: false,
                                id: 'referralPhone',
                                label: 'Referral Phone Number',
                                placeholder: '+1 (123) 456-7890',
                                inputType: 'tel',
                                value: referralPhone,
                                onChange: setReferralPhone,
                              })}
                            </Grid>
                            <Grid item xs={6} mb={2}>
                              {renderMuiFormField({
                                autoFocus: false,
                                required: true,
                                id: 'referralEmail',
                                label: 'Email Address Of Your Referral',
                                placeholder: 'foxmulder@gmail.com',
                                inputType: 'email',
                                value: referralEmail,
                                onChange: setReferralEmail,
                              })}
                            </Grid>
                            <Grid item xs={6}>
                              {renderMuiFormField({
                                autoFocus: false,
                                required: true,
                                id: 'giftCard',
                                label: 'Your Gift Card Preference',
                                placeholder: 'Select a store',
                                inputType: 'select',
                                options: selectData.giftCards,
                                value: giftCard,
                                onChange: setGiftCard,
                              })}
                            </Grid>
                            <Grid item xs={12}>
                              <Typography
                                sx={{ paddingLeft: '5px' }}
                                variant="body2"
                              >
                                Share Your Referral Link
                              </Typography>
                              <Box display="flex" alignItems="center">
                                <Box flexGrow={1}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      border: '1px solid #ddd',
                                      borderRadius: '8px',
                                      padding: '8px 12px',
                                      overflowWrap: 'break-word',
                                      wordBreak: 'break-all',
                                      fontSize: '14px !important',
                                      backgroundColor: '#e9ecef!important',
                                    }}
                                  >
                                    {referralUrl}
                                  </Typography>
                                </Box>
                                <IconButton
                                  onClick={() => {
                                    navigator.clipboard.writeText(referralUrl)
                                    setSnackbarOpen(true)
                                  }}
                                  aria-label="Copy referral link to clipboard"
                                  sx={{
                                    ml: 1,
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    '&:hover': {
                                      backgroundColor: '#efefef',
                                    },
                                  }}
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                                <Snackbar
                                  open={snackbarOpen}
                                  autoHideDuration={2000}
                                  onClose={() => setSnackbarOpen(false)}
                                  message="Referral link copied to clipboard"
                                  anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                  }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12} mt={3}>
                              <Box
                                display={'flex'}
                                flexDirection={'row'}
                                justifyContent={'space-between'}
                              >
                                {/* <Button
                              variant="contained"
                              onClick={() => {
                                setViewEmail(true)
                              }}
                              // disabled={}
                              paddingX={3}
                              sx={{
                                backgroundColor: '#fff',
                                boxShadow: 'none',
                                borderRadius: '100px',
                                border: '1.5px solid #272729',
                                '&:hover, &:focus, &:active, &:focus:not(:hover)':
                                  {
                                    transform: 'none',
                                    backgroundColor: '#fff9',
                                    boxShadow: 'none',
                                  },
                              }}
                            >
                              <Typography
                                variant="body1Medium"
                                textTransform={'capitalize'}
                                color={'#272729'}
                              >
                                View Example Email
                              </Typography>
                            </Button> */}
                                <Button
                                  variant="contained"
                                  // onClick={onSubmit}
                                  // disabled={}
                                  disabled={isDisabled}
                                  onClick={handleSubmit}
                                  sx={{
                                    padding: '8px 24px',
                                    backgroundColor: '#2370ED',
                                    border: '2px solid #2370ED',
                                    color: '#fff',
                                    boxShadow: 'none',
                                    borderRadius: '100px',
                                    textTransform: 'capitalize',
                                    fontWeight: 600,
                                    fontSize: '18px',
                                    lineHeight: 1.5,
                                    '&:hover, &:focus, &:active, &:focus:not(:hover)':
                                      {
                                        transform: 'none',
                                        backgroundColor: '#1c59bd',
                                        borderColor: '#1c59bd',
                                        boxShadow: 'none',
                                      },
                                    '&:disabled': {
                                      borderColor: 'rgba(0,0,0,0)',
                                    },
                                  }}
                                >
                                  Send Referral Email
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                        </>
                      )}
                      {/* {viewEmail && (
                        <>
                          <Typography
                            variant="headerTitle"
                            sx={{ color: '#262627' }}
                          >
                            Example Email
                          </Typography>
                          <Typography
                            variant="body1"
                            mt={1}
                            color={'#495057'}
                            textAlign="left"
                          >
                            Here&apos;s the email we&apos;ll send over to your
                            friend
                          </Typography>
                          <Box
                            mt={4}
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              overflow: 'scroll',
                              height: '385px',
                            }}
                          >
                            <img
                              style={{
                                height: '400%',
                                width: '80%',
                                objectFit: 'fill',
                              }}
                              src={email}
                            />
                          </Box>
                          <Box
                            display={'flex'}
                            flexDirection={'row'}
                            justifyContent={'space-between'}
                            mt={5}
                          >
                            <Button
                              variant="contained"
                              onClick={() => {
                                setViewEmail(false)
                              }}
                              // disabled={}
                              paddingX={3}
                              sx={{
                                backgroundColor: '#fff',
                                boxShadow: 'none',
                                borderRadius: '100px',
                                border: '1.5px solid #272729',
                                '&:hover, &:focus, &:active, &:focus:not(:hover)':
                                  {
                                    transform: 'none',
                                    backgroundColor: '#fff9',
                                    boxShadow: 'none',
                                  },
                              }}
                            >
                              <Typography
                                variant="body1Medium"
                                textTransform={'capitalize'}
                                color={'#272729'}
                              >
                                Back
                              </Typography>
                            </Button>
                            <Button
                              variant="contained"
                              // onClick={onSubmit}
                              // disabled={}
                              paddingX={3}
                              disabled={isDisabled}
                              onClick={handleSubmit}
                              sx={{
                                backgroundColor: '#2370ED',
                                boxShadow: 'none',
                                borderRadius: '100px',
                                '&:hover, &:focus, &:active, &:focus:not(:hover)':
                                  {
                                    transform: 'none',
                                    backgroundColor: '#1c59bd',
                                    boxShadow: 'none',
                                  },
                              }}
                            >
                              <Typography
                                variant="body1Medium"
                                textTransform={'capitalize'}
                                color={'#fff'}
                              >
                                Send Referral Email
                              </Typography>
                            </Button>
                          </Box>
                        </>
                      )} */}
                    </>
                  )}
                  {sent && (
                    <>
                      <Typography
                        variant="headerTitle"
                        sx={{ color: '#262627' }}
                      >
                        Referral Sent Successfully!
                      </Typography>
                      <Typography
                        variant="body1"
                        mt={1}
                        color={'#495057'}
                        textAlign="left"
                      >
                        As soon as your friend meets with a PARCO team member,
                        you will both recieve your gift cards!
                      </Typography>

                      <Box
                        mt={4}
                        sx={{ display: 'flex', justifyContent: 'center' }}
                      >
                        <img style={{ height: '135px' }} src={giftcard} />
                      </Box>

                      <Box
                        display={'flex'}
                        flexDirection={'row'}
                        justifyContent={'space-between'}
                        mt={5}
                      >
                        <Button
                          variant="contained"
                          onClick={() => {
                            onClose()
                          }}
                          sx={{
                            padding: '8px 24px',
                            backgroundColor: '#FFF',
                            border: '2px solid #262627',
                            color: '#262627',
                            boxShadow: 'none',
                            borderRadius: '100px',
                            textTransform: 'capitalize',
                            fontWeight: 600,
                            fontSize: '18px',
                            lineHeight: 1.5,
                            '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                              transform: 'none',
                              backgroundColor: '#f1f1f1',
                              borderColor: '#262627',
                              boxShadow: 'none',
                            },
                            '&:disabled': {
                              borderColor: 'rgba(0,0,0,0)',
                            },
                          }}
                        >
                          Head back to Dashboard
                        </Button>
                        <Button
                          variant="contained"
                          disabled={isDisabled}
                          onClick={() => {
                            setSent(false)
                            setName('')
                            setReferralEmail('')
                            setReferralPhone('')
                            setGiftCard('')
                            setIsDisabled(true)
                          }}
                          sx={{
                            padding: '8px 24px',
                            backgroundColor: '#2370ED',
                            border: '2px solid #2370ED',
                            color: '#fff',
                            boxShadow: 'none',
                            borderRadius: '100px',
                            textTransform: 'capitalize',
                            fontWeight: 600,
                            fontSize: '18px',
                            lineHeight: 1.5,
                            '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                              transform: 'none',
                              backgroundColor: '#1c59bd',
                              borderColor: '#1c59bd',
                              boxShadow: 'none',
                            },
                            '&:disabled': {
                              borderColor: 'rgba(0,0,0,0)',
                            },
                          }}
                        >
                          Refer someone else
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </DialogContent>
      </Dialog>
    </>
  )
}
